import { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Mail, Eye, RefreshCcw, Plus, Trash2, ExternalLink, Send, CheckCircle2, XCircle, Clock, Download, BellRing, Sparkles } from "lucide-react";

const CATEGORIES = [
  { value: "retailer", label: "🛒 Distributeur" },
  { value: "brand", label: "🏷️ Marque" },
  { value: "wholesale", label: "📦 Grossiste" },
  { value: "foundation", label: "❤️ Fondation" },
  { value: "club", label: "⚽ Club" },
];

const STATUSES = [
  { value: "to_contact", label: "À contacter", color: "bg-gray-200 text-gray-800", Icon: Clock },
  { value: "contacted", label: "Contacté", color: "bg-blue-100 text-blue-800", Icon: Send },
  { value: "replied", label: "A répondu", color: "bg-amber-100 text-amber-800", Icon: Mail },
  { value: "accepted", label: "Accepté", color: "bg-green-100 text-green-800", Icon: CheckCircle2 },
  { value: "refused", label: "Refusé", color: "bg-red-100 text-red-800", Icon: XCircle },
];

export default function SportPartnersAdminPanel({ API, pinInput }) {
  const pin = pinInput || "SAFUAYE2024";
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [preview, setPreview] = useState(null);
  const [previewLang, setPreviewLang] = useState("en");
  const [smtpConfigured, setSmtpConfigured] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newP, setNewP] = useState({ name: "", category: "retailer", website: "", email: "", contact_name: "", notes: "" });
  const [selected, setSelected] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkLang, setBulkLang] = useState("auto");
  const [distanceFilter, setDistanceFilter] = useState("all"); // all | walk | drive | remote
  const [followupBusy, setFollowupBusy] = useState(false);
  // ------- Draft IA (Gemini) -------
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftPartner, setDraftPartner] = useState(null);
  const [draftReceived, setDraftReceived] = useState("");
  const [draftIntent, setDraftIntent] = useState("reply");
  const [draftLang, setDraftLang] = useState("auto");
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftResult, setDraftResult] = useState(null); // { subject, body_text, body_html, lang }
  const [draftSending, setDraftSending] = useState(false);

  const openDraftModal = (p) => {
    setDraftPartner(p);
    setDraftReceived("");
    setDraftIntent("reply");
    setDraftLang("auto");
    setDraftResult(null);
    setDraftOpen(true);
  };

  const generateDraft = async () => {
    if (!draftPartner) return;
    try {
      setDraftLoading(true);
      const r = await axios.post(`${API}/ai/draft-partner-reply`, {
        pin,
        partner_id: draftPartner.id,
        partner_message: draftReceived,
        intent: draftIntent,
        lang: draftLang,
      });
      setDraftResult(r.data);
    } catch (e) {
      alert("Erreur IA : " + (e.response?.data?.detail || e.message));
    } finally {
      setDraftLoading(false);
    }
  };

  const sendDraft = async () => {
    if (!draftResult) return;
    if (!smtpConfigured) { alert("⚠️ SMTP non configuré"); return; }
    if (!window.confirm(`Envoyer réellement cet email à ${draftResult.partner_email} ?`)) return;
    try {
      setDraftSending(true);
      const r = await axios.post(`${API}/ai/draft-partner-send`, {
        pin,
        partner_id: draftPartner.id,
        subject: draftResult.subject,
        body_text: draftResult.body_text,
        body_html: draftResult.body_html,
      });
      alert(r.data.sent ? `✅ Envoyé à ${r.data.to}` : `❌ Échec : ${r.data.reason}`);
      if (r.data.sent) {
        setDraftOpen(false);
        load();
      }
    } catch (e) {
      alert("Erreur envoi : " + (e.response?.data?.detail || e.message));
    } finally {
      setDraftSending(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/sport-partners`, { params: { pin } });
      setPartners(r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadSmtpStatus = async () => {
    try {
      const r = await axios.get(`${API}/mission/config-status`, { params: { pin } });
      setSmtpConfigured(r.data?.smtp?.configured || false);
    } catch (e) {
      setSmtpConfigured(false);
    }
  };

  useEffect(() => {
    load();
    loadSmtpStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seed = async (region = "uk") => {
    try {
      const r = await axios.post(`${API}/sport-partners/seed`, null, { params: { pin, region } });
      alert(`✅ Import ${region.toUpperCase()} : ${r.data.inserted} nouveaux partenaires (${r.data.total} au total)`);
      load();
    } catch (e) {
      alert("Erreur import : " + (e.response?.data?.detail || e.message));
    }
  };

  const openPreview = async (p, lang = previewLang) => {
    try {
      const r = await axios.get(`${API}/sport-partners/${p.id}/email-preview`, { params: { pin, lang } });
      setPreview({ ...r.data, id: p.id });
      setPreviewLang(lang);
    } catch (e) {
      alert("Erreur aperçu : " + (e.response?.data?.detail || e.message));
    }
  };

  const sendMail = async (pid, lang) => {
    if (!smtpConfigured) {
      alert("⚠️ SMTP non configuré. Ouvrez /app/backend/.env et renseignez SMTP_USER + SMTP_PASSWORD (mot de passe d'application Gmail).");
      return;
    }
    if (!window.confirm("Envoyer réellement l'email ?")) return;
    try {
      const r = await axios.post(`${API}/sport-partners/${pid}/send`, null, { params: { pin, lang } });
      const ok = r.data.smtp?.sent;
      alert(ok ? `✅ Email envoyé à ${r.data.email}` : `❌ Échec : ${r.data.smtp?.reason}`);
      setPreview(null);
      load();
    } catch (e) {
      alert("Erreur envoi : " + (e.response?.data?.detail || e.message));
    }
  };

  const changeStatus = async (pid, status) => {
    try {
      await axios.patch(`${API}/sport-partners/${pid}`, { status }, { params: { pin } });
      load();
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const remove = async (pid, name) => {
    if (!window.confirm(`Supprimer ${name} ?`)) return;
    try {
      await axios.delete(`${API}/sport-partners/${pid}`, { params: { pin } });
      load();
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  };

  const createNew = async () => {
    if (!newP.name?.trim()) {
      alert("Nom obligatoire");
      return;
    }
    try {
      await axios.post(`${API}/sport-partners`, newP, { params: { pin } });
      setNewP({ name: "", category: "retailer", website: "", email: "", contact_name: "", notes: "" });
      setAddOpen(false);
      load();
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (visibleIds) => {
    setSelected((prev) => {
      const allSelected = visibleIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const bulkSend = async () => {
    if (!smtpConfigured) {
      alert("⚠️ SMTP non configuré.");
      return;
    }
    const ids = Array.from(selected);
    if (ids.length === 0) {
      alert("Aucun partenaire sélectionné");
      return;
    }
    const label = bulkLang === "auto" ? "auto (FR pour FR/BE/CH, EN sinon)" : bulkLang.toUpperCase();
    if (!window.confirm(`Envoyer la lettre à ${ids.length} partenaire(s) en ${label} ?\n\n⚠️ Cette action est irréversible et prendra ~1s par email.`)) return;
    try {
      setBulkSending(true);
      setBulkResult(null);
      const r = await axios.post(`${API}/sport-partners/bulk-send`, { ids, lang: bulkLang }, { params: { pin } });
      setBulkResult(r.data);
      setSelected(new Set());
      load();
    } catch (e) {
      alert("Erreur envoi groupé : " + (e.response?.data?.detail || e.message));
    } finally {
      setBulkSending(false);
    }
  };

  const bulkFollowup = async () => {
    if (!smtpConfigured) { alert("⚠️ SMTP non configuré."); return; }
    if (followUpCount === 0) { alert("Aucun partenaire à relancer."); return; }
    if (!window.confirm(
      `Envoyer un email de relance amical à ${followUpCount} partenaire(s) contacté(s) depuis 7 jours ou plus ?\n\n` +
      `Ils passeront au statut « Relancé ».`
    )) return;
    try {
      setFollowupBusy(true);
      const r = await axios.post(`${API}/sport-partners/followup-send`,
        { ids: [], days: 7 }, { params: { pin } });
      alert(`✅ Relances : ${r.data.sent}/${r.data.total} envoyées.`);
      load();
    } catch (e) {
      alert("Erreur relance : " + (e.response?.data?.detail || e.message));
    } finally { setFollowupBusy(false); }
  };

  const filtered = partners.filter((p) => {
    // Distance
    if (distanceFilter === "walk" && !(p.distance_km !== null && p.distance_km !== undefined && p.distance_km <= 3)) return false;
    if (distanceFilter === "drive" && !(p.distance_km !== null && p.distance_km !== undefined && p.distance_km <= 15)) return false;
    if (distanceFilter === "remote" && p.distance_km !== null && p.distance_km !== undefined) return false;
    // Status/category
    if (filter === "all") return true;
    if (filter === "follow_up") {
      if (p.status !== "contacted") return false;
      const days = p.last_action_at ? Math.floor((Date.now() - new Date(p.last_action_at).getTime()) / 86400000) : 0;
      return days >= 7;
    }
    return p.status === filter || p.category === filter;
  });
  const followUpCount = partners.filter((p) => {
    if (p.status !== "contacted") return false;
    const days = p.last_action_at ? Math.floor((Date.now() - new Date(p.last_action_at).getTime()) / 86400000) : 0;
    return days >= 7;
  }).length;
  const counts = STATUSES.map((s) => ({ ...s, n: partners.filter((p) => p.status === s.value).length }));

  return (
    <div data-testid="sport-partners-panel" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-600" />
            Partenaires Sport UK
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {partners.length} enseignes / marques / fondations · Objectif : équiper l'Académie SAFUAYE U7–U13
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => seed("uk")}
            data-testid="sport-partners-seed-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
          >
            <RefreshCcw className="w-4 h-4" /> Importer UK (39)
          </button>
          <button
            onClick={() => seed("fr")}
            data-testid="sport-partners-seed-fr-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
          >
            <RefreshCcw className="w-4 h-4" /> Importer FR/BE/CH/IT (27)
          </button>
          <button
            onClick={() => seed("sutton")}
            data-testid="sport-partners-seed-sutton-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium"
            title="Commerces sportifs proches du siège UK (SM2 5EN) — Sutton, Belmont, Croydon, Wimbledon"
          >
            <RefreshCcw className="w-4 h-4" /> 📍 Local SM2 5EN (17)
          </button>
          <a
            href={`${API}/sport-partners/export.csv?pin=${encodeURIComponent(pin)}`}
            data-testid="sport-partners-export-csv"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium"
            title="Télécharger tous les partenaires au format CSV (Excel)"
          >
            <Download className="w-4 h-4" /> Export CSV
          </a>
          <button
            onClick={() => setAddOpen(!addOpen)}
            data-testid="sport-partners-add-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>
      </div>

      {/* SMTP status banner */}
      {smtpConfigured === false && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded" data-testid="smtp-warning-banner">
          <p className="text-sm text-amber-900">
            <strong>⚠️ SMTP Gmail non configuré.</strong> Vous pouvez consulter les aperçus des lettres, mais l'envoi automatique
            ne fonctionnera pas tant que <code>SMTP_USER</code> et <code>SMTP_PASSWORD</code> (mot de passe d'application) ne
            sont pas renseignés dans <code>/app/backend/.env</code>.
          </p>
        </div>
      )}
      {smtpConfigured === true && (
        <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded" data-testid="smtp-ok-banner">
          <p className="text-sm text-emerald-900">
            <strong>✅ SMTP Gmail configuré.</strong> Vous pouvez envoyer les demandes automatiquement.
          </p>
        </div>
      )}

      {/* Status counters */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <button
          onClick={() => setFilter("all")}
          className={`p-3 rounded-lg text-center transition ${filter === "all" ? "bg-gray-900 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
        >
          <div className="text-2xl font-bold">{partners.length}</div>
          <div className="text-xs">Tous</div>
        </button>
        {counts.map((s) => (
          <button
            key={s.value}
            onClick={() => setFilter(s.value)}
            data-testid={`filter-${s.value}`}
            className={`p-3 rounded-lg text-center transition ${filter === s.value ? "ring-2 ring-offset-2 ring-amber-500 " + s.color : s.color + " hover:opacity-80"}`}
          >
            <div className="text-2xl font-bold">{s.n}</div>
            <div className="text-xs">{s.label}</div>
          </button>
        ))}
        <button
          onClick={() => setFilter("follow_up")}
          data-testid="filter-follow-up"
          className={`p-3 rounded-lg text-center transition ${filter === "follow_up" ? "ring-2 ring-offset-2 ring-amber-500 bg-amber-500 text-white" : "bg-amber-100 hover:bg-amber-200 text-amber-900"}`}
          title="Partenaires contactés depuis 7 jours ou plus, sans réponse"
        >
          <div className="text-2xl font-bold flex items-center justify-center gap-1">
            <BellRing className="w-5 h-5" /> {followUpCount}
          </div>
          <div className="text-xs">À relancer (7j+)</div>
        </button>
      </div>

      {/* Distance filter (SM2 5EN) + Bulk follow-up */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 flex flex-wrap items-center gap-3" data-testid="distance-filter-bar">
        <span className="text-xs font-semibold uppercase text-gray-500">📍 Distance depuis SM2 5EN :</span>
        {[
          { v: "all", label: "Toutes", color: "gray" },
          { v: "walk", label: "🚶 À pied (≤ 3 km)", color: "green" },
          { v: "drive", label: "🚗 En voiture (≤ 15 km)", color: "blue" },
          { v: "remote", label: "🌍 Distant / national", color: "amber" },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setDistanceFilter(opt.v)}
            data-testid={`distance-filter-${opt.v}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              distanceFilter === opt.v
                ? `bg-${opt.color}-600 text-white shadow`
                : `bg-${opt.color}-100 text-${opt.color}-800 hover:bg-${opt.color}-200`
            }`}
          >{opt.label}</button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {followUpCount > 0 && (
            <button
              onClick={bulkFollowup}
              disabled={followupBusy || !smtpConfigured}
              data-testid="bulk-followup-btn"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${
                followupBusy || !smtpConfigured
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-amber-600 hover:bg-amber-700 text-white"
              }`}
              title="Envoyer un email de relance amicale à tous les partenaires en attente depuis 7 jours ou plus"
            >
              <BellRing className="w-4 h-4" />
              {followupBusy ? "Envoi…" : `🔔 Relancer les ${followUpCount} en attente`}
            </button>
          )}
        </div>
      </div>

      {/* Add form */}
      {addOpen && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3" data-testid="add-partner-form">
          <div className="grid md:grid-cols-2 gap-3">
            <input
              placeholder="Nom *"
              value={newP.name}
              onChange={(e) => setNewP({ ...newP, name: e.target.value })}
              className="px-3 py-2 border rounded"
              data-testid="add-partner-name"
            />
            <select
              value={newP.category}
              onChange={(e) => setNewP({ ...newP, category: e.target.value })}
              className="px-3 py-2 border rounded"
              data-testid="add-partner-category"
            >
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <input
              placeholder="Email"
              value={newP.email}
              onChange={(e) => setNewP({ ...newP, email: e.target.value })}
              className="px-3 py-2 border rounded"
              data-testid="add-partner-email"
            />
            <input
              placeholder="Site web"
              value={newP.website}
              onChange={(e) => setNewP({ ...newP, website: e.target.value })}
              className="px-3 py-2 border rounded"
            />
            <input
              placeholder="Contact (nom)"
              value={newP.contact_name}
              onChange={(e) => setNewP({ ...newP, contact_name: e.target.value })}
              className="px-3 py-2 border rounded"
            />
            <textarea
              placeholder="Notes"
              value={newP.notes}
              onChange={(e) => setNewP({ ...newP, notes: e.target.value })}
              className="px-3 py-2 border rounded md:col-span-2"
              rows={2}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={createNew}
              data-testid="add-partner-save"
              className="px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800"
            >
              Enregistrer
            </button>
            <button
              onClick={() => setAddOpen(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Bulk send results */}
      {bulkResult && (
        <div className="bg-white border rounded-lg p-4" data-testid="bulk-results">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900">
              Résultat de l'envoi groupé — {bulkResult.sent}/{bulkResult.total} envoyés
              {bulkResult.failed > 0 && <span className="text-red-600 ml-2">({bulkResult.failed} en échec)</span>}
            </h4>
            <button onClick={() => setBulkResult(null)} className="text-sm text-gray-500 hover:text-gray-700">
              Fermer
            </button>
          </div>
          <div className="max-h-40 overflow-y-auto text-sm space-y-1">
            {bulkResult.results.map((r) => (
              <div key={r.id} className={`flex justify-between gap-3 px-2 py-1 rounded ${r.sent ? "bg-green-50" : "bg-red-50"}`}>
                <span className="truncate">
                  {r.sent ? "✅" : "❌"} <strong>{r.name || r.id}</strong> — {r.email || "(pas d'email)"}
                </span>
                {!r.sent && <span className="text-xs text-red-600 italic">{r.reason}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
        {selected.size > 0 && (
          <div className="p-3 bg-amber-50 border-b border-amber-200 flex flex-wrap items-center justify-between gap-3" data-testid="bulk-bar">
            <p className="text-sm text-amber-900 font-medium">
              {selected.size} partenaire(s) sélectionné(s)
              <button onClick={() => setSelected(new Set())} className="ml-3 text-xs text-amber-700 hover:underline">
                (tout désélectionner)
              </button>
            </p>
            <div className="flex items-center gap-2">
              <select
                value={bulkLang}
                onChange={(e) => setBulkLang(e.target.value)}
                data-testid="bulk-lang-select"
                className="px-2 py-1.5 text-sm border rounded"
              >
                <option value="auto">Auto (FR pour FR/BE/CH)</option>
                <option value="en">🇬🇧 Toutes en anglais</option>
                <option value="fr">🇫🇷 Toutes en français</option>
              </select>
              <button
                onClick={bulkSend}
                disabled={bulkSending || !smtpConfigured}
                data-testid="bulk-send-btn"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${
                  bulkSending || !smtpConfigured
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-emerald-700 hover:bg-emerald-800 text-white"
                }`}
              >
                <Send className="w-4 h-4" />
                {bulkSending ? "Envoi en cours…" : `Envoyer les ${selected.size} sélectionnés`}
              </button>
            </div>
          </div>
        )}
        <table className="min-w-full text-sm" data-testid="partners-table">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left w-8">
                <input
                  type="checkbox"
                  data-testid="select-all-checkbox"
                  checked={filtered.length > 0 && filtered.every((p) => selected.has(p.id))}
                  onChange={() => toggleSelectAll(filtered.map((p) => p.id))}
                  className="cursor-pointer"
                />
              </th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Nom</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Cat.</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Email</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Statut</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Notes</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-center text-gray-400">Chargement…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-gray-400">Aucun partenaire — cliquez « Importer la liste UK » pour commencer.</td></tr>
            )}
            {filtered.map((p) => {
              const s = STATUSES.find((x) => x.value === p.status) || STATUSES[0];
              const flag = p.country === "FR" ? "🇫🇷" : p.country === "BE" ? "🇧🇪" : p.country === "CH" ? "🇨🇭" : p.country === "IT" ? "🇮🇹" : "🇬🇧";
              // Follow-up: contacted more than 15 days ago and no reply yet
              const daysSince = p.last_action_at
                ? Math.floor((Date.now() - new Date(p.last_action_at).getTime()) / 86400000)
                : null;
              const needsFollowUp = p.status === "contacted" && daysSince !== null && daysSince >= 7;
              return (
                <tr key={p.id} className={`border-b hover:bg-amber-50/40 ${needsFollowUp ? "bg-amber-50/60" : ""}`} data-testid={`partner-row-${p.id}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      data-testid={`select-${p.id}`}
                      checked={selected.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      className="cursor-pointer"
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-900">
                    <span className="mr-1">{flag}</span>
                    {p.name}
                    {p.website && (
                      <a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center ml-2 text-blue-600 hover:text-blue-800">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {needsFollowUp && (
                      <span
                        data-testid={`follow-up-badge-${p.id}`}
                        className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-semibold"
                        title={`Contacté il y a ${daysSince} jours — pas de réponse`}
                      >
                        <BellRing className="w-3 h-3" /> Relancer
                      </span>
                    )}
                    {p.distance_km !== null && p.distance_km !== undefined && (
                      <span
                        data-testid={`distance-badge-${p.id}`}
                        className={`inline-flex items-center gap-0.5 ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          p.distance_km <= 3 ? "bg-green-100 text-green-800"
                          : p.distance_km <= 15 ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-700"
                        }`}
                        title={`Distance à vol d'oiseau depuis SM2 5EN`}
                      >
                        📍 {p.distance_km.toFixed(1)} km
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {(CATEGORIES.find((c) => c.value === p.category) || {}).label || p.category}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {p.email ? <a href={`mailto:${p.email}`} className="text-blue-600 hover:underline">{p.email}</a> : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={p.status}
                      onChange={(e) => changeStatus(p.id, e.target.value)}
                      data-testid={`status-select-${p.id}`}
                      className={`px-2 py-1 rounded text-xs font-medium border-0 ${s.color}`}
                    >
                      {STATUSES.map((st) => <option key={st.value} value={st.value}>{st.label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-gray-500 text-xs max-w-xs">
                    {p.notes || "—"}
                    {p.response_summary && (
                      <div className="mt-1 text-red-600 italic">{p.response_summary}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <button
                      onClick={() => openPreview(p, "en")}
                      data-testid={`preview-en-${p.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs"
                      title="Aperçu lettre EN"
                    >
                      <Eye className="w-3.5 h-3.5" /> EN
                    </button>
                    <button
                      onClick={() => openPreview(p, "fr")}
                      data-testid={`preview-fr-${p.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs"
                      title="Aperçu lettre FR"
                    >
                      <Eye className="w-3.5 h-3.5" /> FR
                    </button>
                    <a
                      href={`${API}/sport-partners/${p.id}/appeal-letter-a5.pdf?pin=${encodeURIComponent(pin)}&lang=en`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`a5-pdf-${p.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-indigo-100 hover:bg-indigo-200 text-indigo-800 text-xs"
                      title="Lettre A5 imprimable pour dépôt en main propre"
                    >
                      📄 A5
                    </a>
                    <button
                      onClick={() => openDraftModal(p)}
                      data-testid={`draft-ai-${p.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-800 text-xs font-medium"
                      title="Générer une réponse contextuelle avec Gemini IA"
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Draft IA
                    </button>
                    <button
                      onClick={() => remove(p.id, p.name)}
                      data-testid={`delete-${p.id}`}
                      className="inline-flex items-center px-2 py-1 rounded bg-red-100 hover:bg-red-200 text-red-800 text-xs"
                      title="Supprimer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Draft IA modal (Gemini) */}
      {draftOpen && draftPartner && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             onClick={() => setDraftOpen(false)} data-testid="draft-ia-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b bg-gradient-to-r from-fuchsia-50 to-amber-50 flex items-center justify-between">
              <div>
                <p className="text-xs text-fuchsia-700 uppercase font-bold flex items-center gap-1">
                  <Sparkles className="w-4 h-4" /> Draft IA — Gemini 3.1 Pro
                </p>
                <h4 className="font-heading font-semibold text-lg text-gray-900">
                  Réponse à {draftPartner.name}
                </h4>
                <p className="text-xs text-gray-500">
                  Signature auto : APEDO Kokou Ognakotan · International President SAFUAYE-UK
                </p>
              </div>
              <button onClick={() => setDraftOpen(false)}
                       className="px-3 py-1.5 text-xs rounded bg-gray-200 hover:bg-gray-300">
                Fermer
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-4">
              {/* Inputs */}
              {!draftResult && (
                <div className="space-y-3" data-testid="draft-ia-inputs">
                  <div>
                    <label className="text-xs font-semibold text-gray-700 uppercase">
                      📥 Message reçu du partenaire (optionnel)
                    </label>
                    <textarea
                      value={draftReceived}
                      onChange={(e) => setDraftReceived(e.target.value)}
                      placeholder="Collez ici le message reçu par email du partenaire, ou laissez vide pour une prise de contact standard."
                      className="mt-1 w-full px-3 py-2 border rounded text-sm"
                      rows={5}
                      data-testid="draft-ia-received"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-700 uppercase">🎯 Intention</label>
                      <select value={draftIntent} onChange={(e) => setDraftIntent(e.target.value)}
                              className="mt-1 w-full px-3 py-2 border rounded text-sm"
                              data-testid="draft-ia-intent">
                        <option value="reply">💬 Répondre au message</option>
                        <option value="thank_you">🙏 Remercier (don / accord)</option>
                        <option value="negotiate">🤝 Négocier / proposer visite</option>
                        <option value="reminder">🔔 Rappel amical</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-700 uppercase">🌍 Langue</label>
                      <select value={draftLang} onChange={(e) => setDraftLang(e.target.value)}
                              className="mt-1 w-full px-3 py-2 border rounded text-sm"
                              data-testid="draft-ia-lang">
                        <option value="auto">Auto (selon pays partenaire)</option>
                        <option value="en">🇬🇧 English</option>
                        <option value="fr">🇫🇷 Français</option>
                        <option value="it">🇮🇹 Italiano</option>
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={generateDraft}
                    disabled={draftLoading}
                    data-testid="draft-ia-generate"
                    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-white ${
                      draftLoading ? "bg-gray-400 cursor-wait" : "bg-fuchsia-700 hover:bg-fuchsia-800"
                    }`}
                  >
                    <Sparkles className="w-4 h-4" />
                    {draftLoading ? "Génération en cours… (10-20 s)" : "✨ Générer la réponse IA"}
                  </button>
                </div>
              )}

              {/* Result */}
              {draftResult && (
                <div className="space-y-3" data-testid="draft-ia-result">
                  <div>
                    <label className="text-xs font-semibold text-gray-700 uppercase">Sujet</label>
                    <input
                      value={draftResult.subject || ""}
                      onChange={(e) => setDraftResult({ ...draftResult, subject: e.target.value })}
                      className="mt-1 w-full px-3 py-2 border rounded text-sm font-medium"
                      data-testid="draft-ia-subject"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-700 uppercase">Corps de l'email (éditable)</label>
                    <textarea
                      value={draftResult.body_text || ""}
                      onChange={(e) => setDraftResult({ ...draftResult, body_text: e.target.value, body_html: null })}
                      className="mt-1 w-full px-3 py-2 border rounded text-sm font-mono"
                      rows={14}
                      data-testid="draft-ia-body"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Vous pouvez modifier ce texte librement avant envoi.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <button
                      onClick={() => setDraftResult(null)}
                      className="px-3 py-2 text-sm rounded bg-gray-200 hover:bg-gray-300"
                    >
                      ← Recommencer
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`Sujet: ${draftResult.subject}\n\n${draftResult.body_text}`);
                        alert("📋 Copié dans le presse-papier");
                      }}
                      data-testid="draft-ia-copy"
                      className="px-3 py-2 text-sm rounded bg-gray-700 hover:bg-gray-800 text-white"
                    >
                      📋 Copier
                    </button>
                    <button
                      onClick={sendDraft}
                      disabled={draftSending || !smtpConfigured}
                      data-testid="draft-ia-send"
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${
                        draftSending || !smtpConfigured
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-emerald-700 hover:bg-emerald-800 text-white"
                      }`}
                      title={smtpConfigured ? `Envoyer à ${draftResult.partner_email}` : "SMTP non configuré"}
                    >
                      <Send className="w-4 h-4" />
                      {draftSending ? "Envoi…" : `Envoyer à ${draftResult.partner_email || "…"}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="preview-modal">
            <div className="p-4 border-b bg-amber-50 flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-800">Aperçu email {preview.lang === "en" ? "🇬🇧 English" : "🇫🇷 Français"}</p>
                <h4 className="font-semibold text-amber-900">{preview.subject}</h4>
                <p className="text-xs text-amber-700 mt-1">À : <strong>{preview.email}</strong></p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => openPreview({ id: preview.id }, preview.lang === "en" ? "fr" : "en")}
                  className="px-3 py-1.5 text-xs rounded bg-gray-200 hover:bg-gray-300"
                >
                  Switch {preview.lang === "en" ? "→ FR" : "→ EN"}
                </button>
                <button onClick={() => setPreview(null)} className="px-3 py-1.5 text-xs rounded bg-gray-200 hover:bg-gray-300">
                  Fermer
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: preview.html }} />
            </div>
            <div className="p-4 border-t bg-gray-50 flex flex-wrap justify-between items-center gap-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`Sujet: ${preview.subject}\n\n${preview.text}`);
                  alert("📋 Copié dans le presse-papier — collez dans Gmail !");
                }}
                data-testid="preview-copy-btn"
                className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-800 text-white text-sm"
              >
                📋 Copier le texte (mode manuel)
              </button>
              <button
                onClick={() => sendMail(preview.id, preview.lang)}
                data-testid="preview-send-btn"
                disabled={!smtpConfigured}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                  smtpConfigured ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
                title={smtpConfigured ? "Envoyer via Gmail SMTP" : "SMTP non configuré"}
              >
                <Send className="w-4 h-4" /> Envoyer réellement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
